/**
 * Orders List Page - iPad-Optimized
 *
 * Displays list of insurance orders
 * Can filter, search, and create new orders
 *
 * Touch targets: All buttons ≥44x44px
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Input, Tag, Typography, message, Spin, Empty, Badge, FloatButton, Modal } from "antd";
import CustomButton from "../components/CustomButton";
import { CustomerSelectModal } from "../components/CustomerSelectModal";
import { useIOSConfirm } from "../hooks";
import {
  SearchOutlined,
  PlusOutlined,
  UserOutlined,
  UserAddOutlined,
  LoadingOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import styles from "./Orders.module.css";
import copyIcon from "../assets/svg/copy.svg";
import {
  getOrderList,
  deleteAdvisorOrder,
  submitOrder,
  createOrder,
  createBasicOrder,
  getOrderDetail,
  convertEmptyStringToNull,
  OrderStatus,
  ORDER_STATUS_DICT,
  countOrdersByStatus,
  OrderAuditStatus,
  RiskAssessmentAuditStatus,
  DatumOrderStatus,
  SubStatusCountOrderStatus,
  useMetadataStore,
} from "@ifa/utils";
import type { OrderListItem, OrderListRequest, OrderStatusCountVO } from "@ifa/utils";

const { Text } = Typography;

interface StatusItem {
  key: string;
  label: string;
  icon: React.ReactNode | null;
  count?: number;
  children?: StatusItem[];
}

export function Orders() {
  const navigate = useNavigate();
  const { confirm } = useIOSConfirm();
  // Filter states
  const [searchText, setSearchText] = useState(() => {
    return sessionStorage.getItem("orders_searchText") || "";
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    return sessionStorage.getItem("orders_statusFilter") || `${DatumOrderStatus.Signature}.${SubStatusCountOrderStatus.PendingSignature}`;
  });

  // Save filter states to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("orders_searchText", searchText);
  }, [searchText]);

  useEffect(() => {
    sessionStorage.setItem("orders_statusFilter", statusFilter);
  }, [statusFilter]);

  // Data states
  const [orderList, setOrderList] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [statusCounts, setStatusCounts] = useState<OrderStatusCountVO[]>([]);
  const [reInsureLoadingId, setReInsureLoadingId] = useState<string | number | null>(null);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [customerSelectVisible, setCustomerSelectVisible] = useState(false);
  const pageSize = 10;
  const { selectMetadataList, fetchMetadata } = useMetadataStore();

  // Pull to refresh & Infinite Scroll refs
  const [pullY, setPullY] = useState(0);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sidebar pull to refresh states
  const [sidebarPullY, setSidebarPullY] = useState(0);
  const sidebarTouchStartY = useRef(0);
  const isSidebarPulling = useRef(false);
  const sidebarListRef = useRef<HTMLDivElement>(null);
  const [sidebarRefreshing, setSidebarRefreshing] = useState(false);

  // Fetch status counts
  const fetchStatusCounts = useCallback(async () => {
    try {
      const res = await countOrdersByStatus();
      setStatusCounts(res || []);
    } catch (error) {
      console.error("Fetch status counts error", error);
    }
  }, []);

  // Initial fetch for status counts
  useEffect(() => {
    fetchStatusCounts();
    fetchMetadata();
  }, [fetchStatusCounts, fetchMetadata]);

  // Fetch data function
  const fetchOrders = useCallback(
    async (isRefresh: boolean, page: number) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const req: OrderListRequest = {
        pageNum: page,
        pageSize: pageSize,
        orderNo: searchText,
        // If searching, we might strictly follow search text.
        // But usually we search WITHIN the status tab.
      };

      // Handle Status Filter
      if (statusFilter) {
        const parts = statusFilter.split(".");
        const parentKey = parts[0] as DatumOrderStatus;
        const childKey = parts[1] as SubStatusCountOrderStatus | undefined;

        req.orderMainStatus = parentKey;
        if (childKey) {
          req.orderSubStatus = childKey;
        }
      }

      try {
        const res = await getOrderList(req, { signal: controller.signal });
        console.log("API Response:", res); // 临时调试
        const list = res.content || [];
        // 修复：从嵌套的 page 对象中获取 totalElements
        const totalCount = res.page?.totalElements || res.totalElements || res.total || 0;
        console.log("Total count:", totalCount, "List length:", list.length); // 临时调试

        if (isRefresh) {
          setOrderList(list);
          setPageNum(1);
          setTotal(totalCount);
        } else {
          setOrderList((prev) => [...prev, ...list]);
          setPageNum(page);
          setTotal(totalCount);
        }
      } catch (error: any) {
        // 忽略请求取消或超时错误
        if (error.name === "AbortError" || error.message === "Canceled" || error.message === "Request timeout" || (typeof error.message === "string" && error.message.includes("timeout"))) {
          return;
        }
        console.error("Fetch error", error);
        // message.error("加載失敗");
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          if (isRefresh) {
            setRefreshing(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [searchText, statusFilter],
  );

  // Trigger refresh when filters change
  useEffect(() => {
    fetchOrders(true, 1);
  }, [fetchOrders]);

  const handleLoadMore = useCallback(() => {
    if (loading || refreshing || orderList.length >= total) return;
    fetchOrders(false, pageNum + 1);
  }, [loading, refreshing, orderList.length, total, pageNum, fetchOrders]);

  const getStatusText = (status: string) => {
    return ORDER_STATUS_DICT[status as OrderStatus] || status;
  };

  const getStatusCount = (key: string): number => {
    // If it's a parent item with children in UI, sum up children counts?
    // Actually, the backend provides the count for the parent group usually.
    // But for "Signature" and "Modify Info", they might not be direct parent statuses in backend response if they are virtual.
    // However, the User JSON implies they are returned as groups.

    // Parse key
    const parts = key.split(".");
    const parentKey = parts[0];
    const childKey = parts[1];

    const parentStats = statusCounts.find((c) => c.orderStatus === parentKey);

    if (!parentStats) return 0;

    if (!childKey) {
      // Return parent count
      return parentStats.count || 0;
    } else {
      // Return child count
      const childStats = parentStats.subStatusCount?.find((c) => c.orderStatus === childKey);
      return childStats?.count || 0;
    }
  };

  // Two-level status structure mapped from metadata
  const statusItems: StatusItem[] = useMemo(() => {
    const orderStatusMetadata = selectMetadataList.find((item) => item.code === "ORDER_STATUS");
    if (!orderStatusMetadata || !orderStatusMetadata.items) {
      return [];
    }

    return orderStatusMetadata.items.map((item: SelectionMetadata) => {
      const children =
        item.children?.map((child: SelectionMetadata) => ({
          key: `${item.key}.${child.key}`,
          label: child.name || "",
          icon: null,
        })) || [];

      return {
        key: item.key || "",
        label: item.name || "",
        icon: "",
        children: children.length > 0 ? children : undefined,
      };
    });
  }, [selectMetadataList]);

  // Check if a status item has children
  const hasChildren = (item: StatusItem) => {
    return item.children && item.children.length > 0;
  };

  // 渲染单个状态项（父项或子项）
  const renderStatusItem = (item: StatusItem, isChild: boolean = false) => {
    const isActive = statusFilter === item.key;
    const handleClick = (e?: React.MouseEvent) => {
      if (isChild) {
        e?.stopPropagation();
        setStatusFilter(item.key);
      } else {
        // 父项：如果有子项则选择第一个子项，否则选择自己
        if (hasChildren(item) && item.children && item.children.length > 0) {
          setStatusFilter(item.children[0].key);
        } else {
          setStatusFilter(item.key);
        }
      }
    };

    const itemClassName = `${styles.statusItem} ${isChild ? styles.childItem : hasChildren(item) ? styles.parentItem : ""} ${isActive ? styles.active : ""}`;

    return (
      <div key={item.key} className={itemClassName} onClick={handleClick}>
        <span className={styles.statusLabel}>{item.label}</span>
        <Badge
          count={getStatusCount(item.key)}
          overflowCount={99}
          color={isActive ? "var(--theme-primary)" : "#F1F5F9"}
          className={styles.statusBadge}
          styles={{
            root: {},
            indicator: {
              fontSize: "12px",

              color: isActive ? "#FFFFFF" : "#64748B",
            },
          }}
        />
      </div>
    );
  };

  // Save scroll position
  useEffect(() => {
    const listElement = listRef.current;
    const handleScroll = () => {
      if (listElement) {
        sessionStorage.setItem("orders_scrollTop", listElement.scrollTop.toString());
      }
    };

    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Restore scroll position
  useEffect(() => {
    const savedScrollTop = sessionStorage.getItem("orders_scrollTop");
    if (savedScrollTop && listRef.current && orderList.length > 0) {
      // Use setTimeout to ensure DOM is rendered
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = parseInt(savedScrollTop, 10);
        }
      }, 100);
    }
  }, [orderList]);

  // Pull to refresh handlers
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 100; // 增加阈值到 100px

    if (isBottom) {
      handleLoadMore();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (listRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isPulling.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    if (diff > 0 && listRef.current?.scrollTop === 0) {
      // Dampen the pull
      setPullY(Math.min(diff * 0.4, 100));
    } else {
      isPulling.current = false;
      setPullY(0);
    }
  };

  const onTouchEnd = async () => {
    isPulling.current = false;
    if (pullY > 60) {
      // Trigger refresh
      fetchOrders(true, 1);
    }
    setPullY(0);
  };

  // Sidebar pull to refresh handlers
  const onSidebarTouchStart = (e: React.TouchEvent) => {
    if (sidebarListRef.current?.scrollTop === 0) {
      sidebarTouchStartY.current = e.touches[0].clientY;
      isSidebarPulling.current = true;
    }
  };

  const onSidebarTouchMove = (e: React.TouchEvent) => {
    if (!isSidebarPulling.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - sidebarTouchStartY.current;
    if (diff > 0 && sidebarListRef.current?.scrollTop === 0) {
      // Dampen the pull
      setSidebarPullY(Math.min(diff * 0.4, 100));
    } else {
      isSidebarPulling.current = false;
      setSidebarPullY(0);
    }
  };

  const onSidebarTouchEnd = async () => {
    isSidebarPulling.current = false;
    if (sidebarPullY > 60) {
      // Trigger refresh
      setSidebarRefreshing(true);
      await fetchStatusCounts();
      setSidebarRefreshing(false);
    }
    setSidebarPullY(0);
  };

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && !loading && !refreshing && orderList.length < total) {
          handleLoadMore();
        }
      },
      {
        // 使用 viewport 作为 root，避免父级 overflow 导致检测失败
        root: null,
        threshold: 0.1,
        rootMargin: "200px",
      },
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => observer.disconnect();
  }, [loading, refreshing, orderList.length, total, handleLoadMore]);

  const handleCreateOrder = () => {
    setIsCreateModalVisible(true);
  };

  const handleCancelCreate = () => {
    setIsCreateModalVisible(false);
  };

  const selectCreateOption = (type: "new" | "existing") => {
    setIsCreateModalVisible(false);
    if (type === "new") {
      navigate("/customers/new", { state: { source: "order" } });
    } else {
      setCustomerSelectVisible(true);
    }
  };

  const handleCustomerSelect = (customer: any) => {
    setCustomerSelectVisible(false);

    // Heuristic to split names from flat structure
    let surnameZh = "";
    let givenNameZh = "";
    if (customer.chineseName) {
      const name = customer.chineseName.trim();
      if (name.length > 0) {
        surnameZh = name.substring(0, 1);
        givenNameZh = name.substring(1);
      }
    }

    let surnameEn = "";
    let givenNameEn = "";
    if (customer.englishName) {
      const parts = customer.englishName.trim().split(/\s+/);
      if (parts.length > 0) {
        // Assuming "GivenName Surname" format
        if (parts.length === 1) {
          surnameEn = parts[0];
        } else {
          surnameEn = parts[parts.length - 1];
          givenNameEn = parts.slice(0, parts.length - 1).join(" ");
        }
      }
    }

    // Map to OrderForm expected format
    const customerData = {
      surnameZh,
      givenNameZh,
      surnameEn,
      givenNameEn,
      gender: customer.gender,
      idType: customer.idType,
      idNumber: customer.idNumber,
      mobileNumber: customer.mobile || customer.phone || "",
    };
    navigate("/orders/new", {
      state: {
        customerData,
        customerId: customer.customerId,
      },
    });
  };

  const handleViewDetail = (order: OrderListItem) => {
    // Draft 和 PendingSignature 跳转到编辑页面
    // 其他所有状态（包括 PendingConfirmation）跳转到查看页面
    if (order.orderStatus === OrderStatus.Draft || order.orderStatus === OrderStatus.PendingSignature) {
      // 草稿和待签署：跳转到编辑页面
      const state = {
        initialStep: 0,
        orderStatus: order.orderStatus,
        orderAuditStatus: order.orderAuditStatus,
      };
      navigate(`/orders/${order.orderId}/edit`, { state });
    } else {
      // 其他状态（包括待确认）：跳转到只读查看页面
      navigate(`/orders/${order.orderId}`, {
        state: {
          orderStatus: order.orderStatus,
          orderAuditStatus: order.orderAuditStatus,
        },
      });
    }
  };

  const handleCorrectionOrder = (orderId: number | string) => {
    navigate(`/orders/${orderId}/correction`);
  };

  const handleCopyOrderNumber = (orderNumber: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    // Check if Clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(orderNumber)
        .then(() => {
          message.success("訂單號碼已複製");
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          message.error("複製失敗");
        });
    } else {
      // Fallback for browsers without Clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = orderNumber;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        if (successful) {
          message.success("訂單號碼已複製");
        } else {
          message.error("複製失敗");
        }
      } catch (err) {
        console.error("Fallback copy failed:", err);
        message.error("複製失敗");
      }

      document.body.removeChild(textArea);
    }
  };

  const handleReInsure = async (orderId: number | string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (reInsureLoadingId === orderId) return;

    const hideLoading = message.loading("正在創建新訂單...", 0);
    try {
      setReInsureLoadingId(orderId);
      const detail: any = await getOrderDetail(orderId);
      // Clean up data for new order
      const cleanData = {
        /**
         * 出生日期
         */
        birthDate: detail.policyholder.basic.birthDate,
        /**
         * 中文名字
         */
        chineseGivenName: detail.policyholder.basic.chineseGivenName,
        /**
         * 中文姓氏
         */
        chineseSurname: detail.policyholder.basic.chineseSurname,
        /**
         * 英文名字
         */
        englishGivenName: detail.policyholder.basic.englishGivenName,
        /**
         * 英文姓氏
         */
        englishSurname: detail.policyholder.basic.englishSurname,
        /**
         * 如果存在交叉销售，是否继续创建订单
         */
        forceAddOrder: detail.forceAddOrder,
        /**
         * 性别
         */
        gender: detail.policyholder.basic.gender,
        /**
         * 身份证明文件号码
         */
        idNumber: detail.policyholder.basic.idNumber,
        /**
         * 身份证明文件类型
         */
        idType: detail.policyholder.basic.idType,
      };
      const payload = {
        ...cleanData,
        draft: true,
        submit: false,
      };
      if (detail.status === OrderStatus.Draft) {
        const res = await createBasicOrder(convertEmptyStringToNull(payload));
        if (res && res.orderId) {
          // Start from beginning for Re-insure/Copy
          navigate(`/orders/${res.orderId}/edit`, {
            state: {
              initialStep: 0,
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      } else {
        const createOrderPayload = {
          policyholder: detail.policyholder,
          insured: detail.insured,
          draft: true,
          submit: false,
        };
        if (
          createOrderPayload.policyholder &&
          detail.policyholder?.sourceId != null
        ) {
          createOrderPayload.policyholder.policyholderId =
            detail.policyholder.sourceId;
        }
        if (
          createOrderPayload.insured &&
          detail.insured?.sourceId != null
        ) {
          createOrderPayload.insured.insuredId = detail.insured.sourceId;
        }

        const res = await createOrder(convertEmptyStringToNull(createOrderPayload));
        if (res) {
          navigate(`/orders/${res}/edit`, {
            state: {
              initialStep: 0,
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoading();
      setReInsureLoadingId(null);
    }
  };

  const handleSubmitDraftOrder = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    confirm({
      title: "確認提交",
      content: "確認要提交此訂單嗎？",
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        try {
          await submitOrder(orderId);
          message.success("訂單提交成功");
          // Refresh list
          fetchOrders(true, 1);
          fetchStatusCounts();
        } catch (error: any) {
          console.error("Submit failed:", error);
          if (error?.message) {
            message.error(error.message);
          } else {
            message.error("提交失敗");
          }
        }
      },
    });
  };

  const handleDeleteOrder = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    confirm({
      title: "確認刪除",
      content: "訂單刪除後不可恢復，確認刪除該訂單嗎？",
      okText: "確認刪除",
      cancelText: "取消",
      onOk: async () => {
        try {
          await deleteAdvisorOrder(orderId);
          message.success("訂單已刪除");
          // Refresh list
          fetchOrders(true, 1);
          fetchStatusCounts();
        } catch (error) {
          console.error("Delete failed:", error);
          // message.error("刪除失敗");
        }
      },
    });
  };

  const handleRiskQuestionnaire = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/orders/${orderId}/risk`);
    // message.info("風險評估問卷功能開發中");
  };

  const renderActionButtons = (order: OrderListItem) => {
    const orderId = order.orderId || 0;
    const status = order.orderStatus;
    const buttons = [];

    // Common: Re-insure (Hidden when Pending Modification or Pending Confirmation)
    if (status !== OrderStatus.Draft && order.orderAuditStatus !== OrderAuditStatus.PendingModification && order.orderAuditStatus !== OrderAuditStatus.PendingConfirmation) {
      const isReInsuring = reInsureLoadingId === orderId;
      buttons.push(
        <CustomButton
          key="reinsure"
          height={36}
          fontSize={14}
          bgColor="transparent"
          color="var(--theme-primary)"
          style={{ border: "1px solid var(--theme-primary)", padding: "0 12px" }}
          className={styles.reInsureBtn}
          onClick={(e: React.MouseEvent) => handleReInsure(orderId, e)}
          loading={isReInsuring}
          disabled={reInsureLoadingId !== null}
        >
          {isReInsuring ? "創建中..." : "再次投保"}
        </CustomButton>,
      );
    }

    // Specific Buttons based on Status
    switch (status) {
      case OrderStatus.Draft:
        // Submit (Edit)
        buttons.push(
          <CustomButton key="submit" height={36} fontSize={14} bgColor="var(--theme-primary)" color="#fff" style={{ padding: "0 12px" }} onClick={(e: React.MouseEvent) => handleSubmitDraftOrder(orderId, e)}>
            提交訂單
          </CustomButton>,
        );
        // Delete - 草稿状态显示删除按钮
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;

      case OrderStatus.PendingConfirmation:
        // 待确认状态显示删除按钮
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;

      case OrderStatus.PendingSignature:
        // 待签署状态显示删除按钮
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;

      case OrderStatus.OrderAudit:
        if (
          order.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.PendingSubmission ||
          order.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.PendingModificationCra ||
          order.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Rejected ||
          order.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Approved ||
          order.orderSubStatus === SubStatusCountOrderStatus.PendingModificationCra
        ) {
          buttons.push(
            <CustomButton key="risk" height={36} fontSize={14} bgColor="var(--theme-primary)" color="#fff" style={{ padding: "0 12px" }} onClick={(e: React.MouseEvent) => handleRiskQuestionnaire(orderId, e)}>
              風險評估問卷
            </CustomButton>,
          );
        }

        if (order.orderAuditStatus === OrderAuditStatus.PendingModification || order.orderAuditStatus === OrderAuditStatus.PendingConfirmation) {
          buttons.push(
            <CustomButton
              key="modify"
              height={36}
              fontSize={14}
              bgColor="var(--theme-primary)"
              color="#fff"
              style={{ padding: "0 12px" }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleCorrectionOrder(orderId);
              }}
            >
              修改訂單
            </CustomButton>,
          );
        }
        break;

      case OrderStatus.Expired:
        buttons.unshift(
          <CustomButton
            key="delete"
            height={36}
            fontSize={14}
            bgColor="transparent"
            color="#ff4d4f"
            style={{ border: "1px solid #ff4d4f", padding: "0 12px" }}
            onClick={(e: React.MouseEvent) => handleDeleteOrder(orderId, e)}
          >
            刪除
          </CustomButton>,
        );
        break;
    }

    return buttons;
  };

  return (
    <div className={styles.ordersLayout}>
      {/* Top Header Row */}
      <header className={styles.ordersPageHeader}>
        <div className={styles.ordersPageHeaderLeft}>訂單管理</div>
        <div className={styles.ordersPageHeaderRight}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className={styles.filterResultsText}>篩選結果</div>
            {/* <Button
              type="text"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              disabled={refreshing}
            /> */}
          </div>
          <Input
            placeholder="搜尋⋯⋯"
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            className={styles.ordersSearchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            allowClear
          />
        </div>
      </header>

      <div className={styles.ordersContentBody}>
        {/* Left Sidebar - Status Filter */}
        <aside className={styles.ordersSidebar}>
          {/* Status List */}
          <div className={styles.statusList} ref={sidebarListRef} onTouchStart={onSidebarTouchStart} onTouchMove={onSidebarTouchMove} onTouchEnd={onSidebarTouchEnd}>
            {/* Pull to refresh indicator */}
            <div
              style={{
                height: sidebarPullY,
                overflow: "hidden",
                transition: isSidebarPulling.current ? "none" : "height 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: 12,
              }}
            >
              {sidebarPullY > 60 ? (
                <>
                  <ArrowUpOutlined rotate={180} />
                  <span style={{ marginLeft: 8 }}>釋放刷新</span>
                </>
              ) : (
                <>
                  <ArrowDownOutlined />
                  <span style={{ marginLeft: 8 }}>下拉刷新</span>
                </>
              )}
            </div>

            {/* Loading overlay */}
            {sidebarRefreshing && (
              <div
                style={{
                  textAlign: "center",
                  padding: "8px 0",
                  color: "#999",
                  fontSize: 12,
                }}
              >
                <Spin size="small" />
                <span style={{ marginLeft: 8 }}>刷新中...</span>
              </div>
            )}

            {statusItems.map((item) => (
              <div key={item.key}>
                {/* 父项 */}
                {renderStatusItem(item, false)}

                {/* 子项列表 */}
                {hasChildren(item) && <div className={styles.statusChildren}>{item.children!.map((child) => renderStatusItem(child, true))}</div>}
              </div>
            ))}
          </div>

          {/* New Order Button at Bottom */}
        </aside>

        {/* Right Content */}
        <main className={styles.ordersMain}>
          {/* Scrollable Orders List Container */}
          <div className={styles.ordersListWrapper}>
            {refreshing && (
              <div className={styles.ordersLoadingOverlay}>
                <Spin tip="刷新中⋯⋯" />
              </div>
            )}
            <div className={styles.ordersList} ref={listRef} onScroll={onScroll} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
              <div
                style={{
                  height: pullY,
                  overflow: "hidden",
                  transition: isPulling.current ? "none" : "height 0.3s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#999",
                }}
              >
                {pullY > 60 ? <ArrowUpOutlined rotate={180} /> : <ArrowDownOutlined />}
                <span style={{ marginLeft: 8 }}>{pullY > 60 ? "釋放刷新" : "下拉刷新"}</span>
              </div>

              <div className={styles.ordersGrid}>
                {orderList.map((order) => (
                  <div key={order.orderId} className={styles.orderCard} onClick={() => handleViewDetail(order)}>
                    <div className={styles.orderCardContent}>
                      {/* Top Section: Order Number + Copy + Status Tag */}
                      <div className={styles.orderTopSection}>
                        <div className={styles.orderTopLeft}>
                          <div className={styles.orderNumber}>{order.orderNo}</div>
                          <CustomButton
                            width={24}
                            height={20}
                            bgColor="transparent"
                            color="#999"
                            icon={<img src={copyIcon} alt="copy" style={{ width: 16, height: 16 }} />}
                            className={styles.copyOrderNumberBtn}
                            onClick={(e: React.MouseEvent) => handleCopyOrderNumber(String(order.orderNo || ""), e)}
                          />
                        </div>
                        <Tag className={`${styles.orderStatusTag} ${styles[`status${order.orderStatus?.replace(/_/g, "")}`] || ""}`}>{getStatusText(order.orderStatus || "")}</Tag>
                      </div>

                      {/* Middle Section: Policyholder + Product + 3-Col Info */}
                      <div className={styles.orderMiddleSection}>
                        {/* Row 1: Policyholder Name */}
                        {/* <div className={styles.orderRowPolicyholder}>
                          <Text type="secondary" style={{ fontSize: 14 }}>
                            {order.policyholderChineseName || order.policyholderEnglishName || "N/A"}
                          </Text>
                        </div> */}

                        {/* Row 2: Product Name */}
                        <div className={`${styles.orderRowProduct} mt`}>
                          <div className={styles.productName}>
                            {order?.productPlans?.[0]?.productName || order.insurance || "未選擇產品"}
                            {order?.productPlans?.[0]?.contributionYears ? `(${order?.productPlans?.[0]?.contributionYears}年)` : ""}
                          </div>
                        </div>

                        {/* Row 3: 3 Columns Info */}
                        <div className={styles.orderInfoGrid}>
                          <div className={styles.infoItem}>
                            <div className={styles.infoLabel}>客戶</div>
                            <div className={styles.infoValue}>{order.policyholderChineseName || order.policyholderEnglishName || "N/A"}</div>
                          </div>

                          <div className={styles.infoItem}>
                            <div className={styles.infoLabel}>首期保費</div>
                            <div className={`${styles.infoValue} ${styles.premiumValue}`}>
                              {order?.productPlans?.[0]?.premiumPerPeriod ? order?.productPlans?.[0]?.premiumPerPeriod.toLocaleString() : "--"}
                              {order?.productPlans?.[0]?.currency && <span className={styles.currencySuffix}>{order?.productPlans?.[0]?.currency}</span>}
                            </div>
                          </div>
                          <div className={styles.infoItem}>
                            <div className={styles.infoLabel}>創建時間</div>
                            <div className={styles.infoValue}>{order.createTime ? dayjs(order.createTime).format("YY/MM/DD HH:mm") : "N/A"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Section: Date + Action Buttons */}
                      <div className={styles.orderBottomSection}>
                        <Text type="secondary" className={styles.orderDateText}></Text>
                        <div style={{ display: "flex", gap: 12 }}>{renderActionButtons(order)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div ref={loadMoreRef} style={{ height: 50, width: "100%" }} />

              {/* Loading indicator */}
              {loading && (
                <div className={styles.loadMoreIndicator} style={{ textAlign: "center", padding: "10px" }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
              )}
              {!loading && orderList.length < total && (
                <div className={styles.loadMoreIndicator}>
                  <Text type="secondary">向下滾動載入更多⋯⋯</Text>
                </div>
              )}

              {/* Empty state */}
              {!loading && !refreshing && orderList.length === 0 && (
                <div className={styles.emptyState}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暫無符合條件的訂單" />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      <Modal title="建立新訂單" open={isCreateModalVisible} onCancel={handleCancelCreate} footer={null} width={600} className="create-order-modal" centered>
        <div className={styles.modalSubtitle}>請選擇客戶來源以開始投保流程</div>

        <div className={styles.createOptionsContainer}>
          <div className={`${styles.createOptionCard} ${styles.newCustomer}`} onClick={() => selectCreateOption("new")}>
            <div className={styles.optionIconWrapper}>
              <UserAddOutlined />
            </div>
            <div className={styles.optionContent}>
              <div className={styles.optionTitle}>新客戶投保</div>
              <div className={styles.optionDesc}>建立全新的客戶檔案，並為其開啟投保申請流程。</div>
            </div>
            <div className={styles.optionArrow}>
              <RightOutlined />
            </div>
          </div>

          <div className={`${styles.createOptionCard} ${styles.existingCustomer}`} onClick={() => selectCreateOption("existing")}>
            <div className={styles.optionIconWrapper}>
              <UserOutlined />
            </div>
            <div className={styles.optionContent}>
              <div className={styles.optionTitle}>現有客戶投保</div>
              <div className={styles.optionDesc}>選擇既有客戶，快速建立訂單。</div>
            </div>
            <div className={styles.optionArrow}>
              <RightOutlined />
            </div>
          </div>
        </div>

        <div className={styles.modalFooterNote}>
          <SafetyCertificateOutlined className={styles.noteIcon} />
          <span>所有建立的訂單將自動進入草稿狀態。請確保在提交前完成所有合規性檢查 (KYC)。</span>
        </div>
      </Modal>

      <CustomerSelectModal visible={customerSelectVisible} onCancel={() => setCustomerSelectVisible(false)} onSelect={handleCustomerSelect} />

      {/* Floating Action Button - New Order */}
      <FloatButton
        icon={<PlusOutlined />}
        type="primary"
        style={{
          width: 64,
          height: 64,
          bottom: 56,
          right: 48,
          backgroundColor: "var(--theme-primary)",
          boxShadow: "var(--theme-shadow-large)",
        }}
        onClick={handleCreateOrder}
      />
    </div>
  );
}
